import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    area,
    booleanIntersects,
    booleanOverlap,
    booleanWithin,
    centroid,
    featureCollection,
    intersect,
    lineString,
    point,
    pointToLineDistance,
    polygon,
} from '@turf/turf';
import { CodaltComponent } from 'app/codalt.component';
import { LocalStorage } from '../local-storage.class';
import { ExportService } from '../services/export/export.service';
import { WorkareaService } from '../services/workarea/workarea.service';
import { Workarea } from '../workarea.class';

@Component({
    selector: 'app-map-measurements',
    templateUrl: './map-measurements.component.html',
    styleUrls: ['./map-measurements.component.scss'],
    standalone: false,
})
export class MapMeasurementsComponent extends CodaltComponent implements OnInit, OnChanges {
    @Input() googleMap;
    @Input() measuring: boolean = false;
    @Input() selectablePoints: Workarea[] = [];
    @Input() projectId = undefined;
    @Output() measuringChange = new EventEmitter<boolean>();
    @Output() selectedPointsChange = new EventEmitter<Workarea[]>();
    @Output() openExportDialog = new EventEmitter<void>();
    @Output() openTransferDialog = new EventEmitter<void>();

    measurementPath: google.maps.Polyline | null = null;
    measurementPolygon: google.maps.Polygon | null = null;
    measurementMarkers: google.maps.Marker[] = [];
    measurementCoordinates: google.maps.LatLng[] = [];

    // Support for holes/uitsnedes
    measurementOuterRing: google.maps.LatLng[] = [];
    measurementHoles: google.maps.LatLng[][] = [];
    holeMarkers: google.maps.Marker[][] = [];
    holePolylines: google.maps.Polyline[] = []; // Separate polylines for orange hole outlines
    excludedSegmentPolylines: google.maps.Polyline[] = [];
    centroidMarker: google.maps.Marker | null = null;
    segmentLabels: google.maps.OverlayView[] = [];
    holeSegmentLabels: google.maps.OverlayView[][] = [];
    isCreatingHole: boolean = false;
    currentHoleIndex: number = 0;
    labelsVisible: boolean = true;
    selectedPointsInPolygon: Workarea[] = [];
    exportingSelectionExcel: boolean = false;

    measurementResult = {
        distance: 0,
        area: 0,
        segmentDistances: [] as number[],
        totalSegments: 0,
        holeSegmentDistances: [] as number[][],
        centroid: null as google.maps.LatLng | null,
    };

    excludedSegmentIndices = new Set<number>();

    copyLongestLengthString(): void {
        if (this.measurementResult.segmentDistances.length !== 4) {
            return;
        }

        const sorted = [...this.measurementResult.segmentDistances].sort((a, b) => b - a);

        const length = sorted[0].toFixed(2);
        const width = sorted[2].toFixed(2);

        const str = `${length}x${width}`;

        if (!str) {
            return;
        }
        navigator.clipboard.writeText(str);
    }

    toggleLabelsVisibility(): void {
        this.labelsVisible = !this.labelsVisible;
        const map = this.labelsVisible ? this.googleMap.googleMap : null;

        this.segmentLabels.forEach(label => label.setMap(map));
        this.holeSegmentLabels.forEach(holeLabelArray => holeLabelArray.forEach(label => label.setMap(map)));
    }

    constructor(
        private cdref: ChangeDetectorRef,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private workareaService: WorkareaService,
        private exportService: ExportService
    ) {
        super();
    }

    ngOnInit(): void {
        this.setupMeasurementClickHandler();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectablePoints'] && this.measurementOuterRing.length >= 3) {
            this.updateSelectedPointsInPolygon();
        }
    }

    // Measurement functionality methods
    enableAreaMeasurement() {
        this.clearMeasurement();
        this.measuring = true;
        this.isCreatingHole = false;
    }

    disableMeasurement() {
        this.clearMeasurement();
        this.measuring = false;
        this.measuringChange.emit(false);
    }

    exportSelectedPointsToExcel(): void {
        if (this.exportingSelectionExcel) {
            return;
        }

        const selectedIds = Array.from(new Set(this.selectedPointsInPolygon.map(point => point.id).filter(Boolean)));
        if (!selectedIds.length) {
            return;
        }

        this.exportingSelectionExcel = true;
        this.exportService.downloadExcelWorkareas(this.projectId, selectedIds).subscribe({
            next: () => {
                this.exportingSelectionExcel = false;
            },
            error: () => {
                this.exportingSelectionExcel = false;
            },
        });
    }

    // Hole/uitsnede management methods
    toggleHoleMode() {
        if (this.measurementOuterRing.length < 3) {
            return; // Need at least 3 points for outer ring before creating holes
        }
        this.isCreatingHole = !this.isCreatingHole;
        if (this.isCreatingHole && this.measurementHoles.length === 0) {
            this.measurementHoles.push([]);
            this.holeMarkers.push([]);
            this.currentHoleIndex = 0;
        }
    }

    addNewHole() {
        if (this.measurementOuterRing.length < 3) {
            return;
        }
        this.measurementHoles.push([]);
        this.holeMarkers.push([]);
        this.currentHoleIndex = this.measurementHoles.length - 1;
        this.isCreatingHole = true;
    }

    saveMeasurementAsWorkarea() {
        if (this.measurementOuterRing.length < 3) {
            return;
        }

        const outerRingCoords = this.measurementOuterRing.map(point => [point.lng(), point.lat()]);
        outerRingCoords.push(outerRingCoords[0]);

        // Only include holes that are inside or partially inside the outer polygon
        const holeCoordsArray: number[][][] = [];

        this.measurementHoles.forEach(hole => {
            if (hole.length < 3) {
                return; // Skip incomplete holes
            }

            // Check if hole is completely inside
            const allPointsInside = hole.every(point => this.isPointInsidePolygon(point, this.measurementOuterRing));

            if (allPointsInside) {
                // Hole is completely inside, use as-is
                const coords = hole.map(point => [point.lng(), point.lat()]);
                coords.push(coords[0]);
                holeCoordsArray.push(coords);
                return;
            }

            // Check if any point is inside (partial overlap)
            const anyPointInside = hole.some(point => this.isPointInsidePolygon(point, this.measurementOuterRing));

            if (!anyPointInside) {
                // Hole is completely outside, skip it
                return;
            }

            // Hole partially overlaps - clip it to outer polygon using turf
            try {
                const outerPolygon = polygon([outerRingCoords]);
                const holeCoords = hole.map(point => [point.lng(), point.lat()]);
                holeCoords.push(holeCoords[0]);
                const holePolygon = polygon([holeCoords]);

                const intersection = intersect(featureCollection([holePolygon, outerPolygon]));

                if (intersection && intersection.geometry.type === 'Polygon') {
                    // Use the intersected polygon coordinates
                    holeCoordsArray.push(intersection.geometry.coordinates[0]);
                } else if (intersection && intersection.geometry.type === 'MultiPolygon') {
                    // If intersection results in multiple polygons, use all of them as separate holes
                    intersection.geometry.coordinates.forEach(polyCoords => holeCoordsArray.push(polyCoords[0]));
                }
            } catch (error) {
                console.warn('Error clipping hole to outer polygon:', error);
                // Fallback: only include points that are inside
                const pointsInside = hole.filter(point => this.isPointInsidePolygon(point, this.measurementOuterRing));
                if (pointsInside.length >= 3) {
                    const coords = pointsInside.map(point => [point.lng(), point.lat()]);
                    coords.push(coords[0]);
                    holeCoordsArray.push(coords);
                }
            }
        });

        const geojson = {
            type: 'Polygon',
            coordinates: [outerRingCoords, ...holeCoordsArray],
        };

        // Convert centroid to plain object
        const centroid = this.measurementResult.centroid
            ? {
                  lat: this.measurementResult.centroid.lat(),
                  lng: this.measurementResult.centroid.lng(),
              }
            : null;

        this.workareaService.setPendingPolygon(geojson, this.measurementResult.area, centroid);

        // Clear measurements after saving
        this.clearMeasurement();
        this.measuring = false;
        this.measuringChange.emit(false);

        const customer = LocalStorage.getCustomer();
        const route = [this.Routenames.customers, customer.id, this.Routenames.workareas, -1];
        const params: any = {
            project: this.activatedRoute.snapshot.queryParams['project'],
        };

        this.router.navigate(route, {
            queryParams: params,
            queryParamsHandling: 'merge',
        });
    }

    private setupMeasurementClickHandler() {
        this.subscriptions.add(
            this.googleMap.mapClick.subscribe((event: google.maps.MapMouseEvent) => {
                if (this.measuring && event.latLng) {
                    this.addMeasurementPoint(event.latLng);
                }
            })
        );
    }

    private addMeasurementPoint(latLng: google.maps.LatLng) {
        if (this.isCreatingHole) {
            this.addHolePoint(latLng);
        } else {
            this.addOuterRingPoint(latLng);
        }
    }

    private addOuterRingPoint(latLng: google.maps.LatLng) {
        this.measurementCoordinates.push(latLng);
        this.measurementOuterRing.push(latLng);

        const markerIndex = this.measurementCoordinates.length - 1;
        const marker = this.createMarker(latLng, 'outer', markerIndex);
        this.measurementMarkers.push(marker);

        if (this.measuring) {
            this.updateAreaMeasurement();
        }
    }

    private addHolePoint(latLng: google.maps.LatLng) {
        if (this.currentHoleIndex >= this.measurementHoles.length) {
            return;
        }

        this.measurementHoles[this.currentHoleIndex].push(latLng);
        const holePointIndex = this.measurementHoles[this.currentHoleIndex].length - 1;

        const marker = this.createMarker(latLng, 'hole', holePointIndex, this.currentHoleIndex);
        this.holeMarkers[this.currentHoleIndex].push(marker);

        if (this.measuring) {
            this.updateAreaMeasurement();
        }
    }

    private addGenericMarkerListeners(
        marker: google.maps.Marker,
        config: {
            onDrag: (latLng: google.maps.LatLng | null) => void;
            onDragEnd: (latLng: google.maps.LatLng | null) => void;
            onRightClick: () => void;
            clearExistingListeners?: boolean;
        }
    ): void {
        // Clear existing listeners if requested
        if (config.clearExistingListeners) {
            google.maps.event.clearInstanceListeners(marker);
        }

        marker.addListener('dragstart', () => {
            marker.setCursor('grabbing');
        });

        marker.addListener('drag', (event: google.maps.MapMouseEvent) => {
            config.onDrag(event.latLng);
        });

        marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
            marker.setCursor('grab');
            config.onDragEnd(event.latLng);
        });

        marker.addListener('rightclick', () => {
            config.onRightClick();
        });
    }

    private updateAreaMeasurement() {
        // Clear existing shapes
        if (this.measurementPolygon) {
            this.measurementPolygon.setMap(null);
        }
        if (this.measurementPath) {
            this.measurementPath.setMap(null);
        }
        // Clear existing hole polylines
        this.holePolylines.forEach(polyline => polyline.setMap(null));
        this.holePolylines = [];
        this.clearExcludedSegmentPolylines();

        if (this.measurementOuterRing.length < 2) {
            this.updateSelectedPointsInPolygon();
            return;
        }

        if (this.measurementOuterRing.length === 2) {
            // Show line for 2 points
            this.measurementPath = new google.maps.Polyline({
                path: this.measurementOuterRing,
                geodesic: true,
                strokeColor: '#28288C',
                strokeOpacity: 1.0,
                strokeWeight: 3,
                map: this.googleMap.googleMap,
            });

            this.measurementPath.addListener('rightclick', (event: google.maps.PolyMouseEvent) => {
                if (!event.latLng) {
                    return;
                }

                if (event.domEvent) {
                    event.domEvent.preventDefault();
                    event.domEvent.stopPropagation();
                }

                const segmentIndex = this.getNearestOuterSegmentIndex(event.latLng);
                if (segmentIndex === null) {
                    return;
                }

                this.toggleSegmentExclusion(segmentIndex);
            });

            this.measurementResult.distance = this.calculateTotalDistance();
            this.renderExcludedSegmentPolylines();
            this.updateSelectedPointsInPolygon();
            return;
        }

        // Create main polygon with correct winding order for proper holes
        const polygonPaths = [this.ensureClockwise(this.measurementOuterRing)]; // Outer ring clockwise

        // Add completed holes (3+ points) with counter-clockwise winding
        const completedHoles: google.maps.LatLng[][] = [];
        this.measurementHoles.forEach(hole => {
            if (hole.length >= 3) {
                const counterClockwiseHole = this.ensureCounterClockwise(hole); // Holes counter-clockwise
                polygonPaths.push(counterClockwiseHole);
                completedHoles.push(counterClockwiseHole);
            }
        });

        // Create the main polygon with real holes
        this.measurementPolygon = new google.maps.Polygon({
            paths: polygonPaths, // Includes outer ring AND holes for real cutouts
            strokeColor: '#28288C',
            strokeOpacity: 0.8,
            strokeWeight: 3,
            fillColor: '#28288C',
            fillOpacity: 0.2,
            clickable: true,
            map: this.googleMap.googleMap,
        });

        // Create separate orange polylines for hole outlines and incomplete holes
        this.measurementHoles.forEach(hole => {
            if (hole.length >= 2) {
                // Create closed path for holes with 3+ points, open path for 2 points
                let holePath = [...hole];
                if (hole.length >= 3) {
                    holePath.push(hole[0]); // Close the path
                }

                const holePolyline = new google.maps.Polyline({
                    path: holePath,
                    geodesic: true,
                    strokeColor: '#FF6B35', // Orange color for holes
                    strokeOpacity: 1.0,
                    strokeWeight: 3,
                    clickable: true,
                    map: this.googleMap.googleMap,
                });

                // Add click listener to hole polylines as well
                holePolyline.addListener('click', (event: google.maps.PolyMouseEvent) => {
                    if (this.isCreatingHole && event.latLng) {
                        this.addHolePointFromPolygonClick(event.latLng);
                    }
                });

                this.holePolylines.push(holePolyline);
            }
        });

        // Add click listener for main polygon
        this.measurementPolygon.addListener('click', (event: google.maps.PolyMouseEvent) => {
            if (event.latLng) {
                if (this.isCreatingHole) {
                    this.addHolePointFromPolygonClick(event.latLng);
                } else {
                    // Add point to outer ring when not creating holes
                    this.addOuterRingPointFromPolygonClick(event.latLng);
                }
            }
        });

        this.measurementPolygon.addListener('rightclick', (event: google.maps.PolyMouseEvent) => {
            if (event.domEvent) {
                event.domEvent.stopPropagation(); // Prevent map click from firing
            }
            if (!event.latLng) {
                return;
            }

            if (event.domEvent) {
                event.domEvent.preventDefault();
                event.domEvent.stopPropagation();
            }

            const segmentIndex = this.getNearestOuterSegmentIndex(event.latLng);
            if (segmentIndex === null) {
                return;
            }

            this.toggleSegmentExclusion(segmentIndex);
        });

        // Add mouseover/mouseout for visual feedback
        if (this.isCreatingHole) {
            this.measurementPolygon.addListener('mouseover', () => {
                if (this.measurementPolygon) {
                    this.measurementPolygon.setOptions({
                        fillOpacity: 0.4,
                        strokeWeight: 4,
                    });
                }
            });

            this.measurementPolygon.addListener('mouseout', () => {
                if (this.measurementPolygon) {
                    this.measurementPolygon.setOptions({
                        fillOpacity: 0.2,
                        strokeWeight: 3,
                    });
                }
            });
        } else {
            // Visual feedback for adding points to outer ring
            this.measurementPolygon.addListener('mouseover', () => {
                if (this.measurementPolygon) {
                    this.measurementPolygon.setOptions({
                        fillOpacity: 0.3,
                        strokeWeight: 4,
                        strokeColor: '#1E7FCB', // Slightly different blue to indicate outer ring mode
                    });
                }
            });

            this.measurementPolygon.addListener('mouseout', () => {
                if (this.measurementPolygon) {
                    this.measurementPolygon.setOptions({
                        fillOpacity: 0.2,
                        strokeWeight: 3,
                        strokeColor: '#28288C',
                    });
                }
            });
        }

        this.measurementResult.area = this.calculatePolygonAreaWithHoles();
        this.measurementResult.distance = this.calculatePerimeter();
        this.calculateHoleSegmentDistances();
        this.calculateAndDisplayCentroid();

        this.createSegmentLabels();
        this.renderExcludedSegmentPolylines();
        this.updateSelectedPointsInPolygon();
    }

    private updateSelectedPointsInPolygon(): void {
        if (this.measurementOuterRing.length < 3 || !this.selectablePoints?.length) {
            this.selectedPointsInPolygon = [];
            this.selectedPointsChange.emit([]);
            return;
        }

        const completedHoles = this.measurementHoles.filter(hole => hole.length >= 3);

        this.selectedPointsInPolygon = this.selectablePoints.filter(point => {
            const pointLatLng = this.getPointLatLng(point);
            if (!pointLatLng) {
                return false;
            }

            if (!this.isPointInsidePolygon(pointLatLng, this.measurementOuterRing)) {
                return false;
            }

            return !completedHoles.some(hole => this.isPointInsidePolygon(pointLatLng, hole));
        });

        this.selectedPointsChange.emit(this.selectedPointsInPolygon);
    }

    private getPointLatLng(point: Workarea): google.maps.LatLng | null {
        if (point?.lat === undefined || point?.lng === undefined || point?.lat === null || point?.lng === null) {
            return null;
        }

        const lat = Number(point.lat);
        const lng = Number(point.lng);
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return null;
        }

        return new google.maps.LatLng(lat, lng);
    }

    private calculateTotalDistance(): number {
        let totalDistance = 0;
        this.measurementResult.segmentDistances = [];

        for (let i = 1; i < this.measurementOuterRing.length; i++) {
            const segmentDistance = google.maps.geometry.spherical.computeDistanceBetween(
                this.measurementOuterRing[i - 1],
                this.measurementOuterRing[i]
            );
            this.measurementResult.segmentDistances.push(segmentDistance);
            totalDistance += segmentDistance;
        }

        this.measurementResult.totalSegments = this.measurementResult.segmentDistances.length;
        this.pruneExcludedSegments();
        return this.calculateIncludedDistance();
    }

    private calculatePerimeter(): number {
        if (this.measurementOuterRing.length < 3) {
            return this.calculateTotalDistance();
        }

        let perimeter = this.calculateTotalDistance();

        const closingSegmentDistance = google.maps.geometry.spherical.computeDistanceBetween(
            this.measurementOuterRing[this.measurementOuterRing.length - 1],
            this.measurementOuterRing[0]
        );

        this.measurementResult.segmentDistances.push(closingSegmentDistance);
        this.measurementResult.totalSegments = this.measurementResult.segmentDistances.length;

        perimeter += closingSegmentDistance;
        this.pruneExcludedSegments();
        return this.calculateIncludedDistance();
    }

    private calculateIncludedDistance(): number {
        return this.measurementResult.segmentDistances.reduce((total, distance, index) => {
            if (this.excludedSegmentIndices.has(index)) {
                return total;
            }
            return total + distance;
        }, 0);
    }

    private getNearestOuterSegmentIndex(point: google.maps.LatLng): number | null {
        if (this.measurementOuterRing.length < 2) {
            return null;
        }

        const points = this.measurementOuterRing;
        const lastIndex = points.length - 1;
        let closestIndex = 0;
        let closestDistance = Number.MAX_VALUE;

        for (let i = 0; i < lastIndex; i++) {
            const distance = this.distanceToLineSegment(point, points[i], points[i + 1]);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = i;
            }
        }

        if (points.length >= 3) {
            const closingDistance = this.distanceToLineSegment(point, points[lastIndex], points[0]);
            if (closingDistance < closestDistance) {
                closestIndex = lastIndex;
            }
        }

        return closestIndex;
    }

    private pruneExcludedSegments(): void {
        const totalSegments = this.measurementResult.segmentDistances.length;
        if (totalSegments === 0) {
            this.excludedSegmentIndices.clear();
            return;
        }

        for (const index of Array.from(this.excludedSegmentIndices)) {
            if (index < 0 || index >= totalSegments) {
                this.excludedSegmentIndices.delete(index);
            }
        }
    }

    toggleSegmentExclusionAll(event?: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const excludeAll = this.excludedSegmentIndices.has(0);
        this.measurementResult.segmentDistances.forEach((_, index) => {
            if (excludeAll) {
                this.excludedSegmentIndices.delete(index);
            } else {
                this.excludedSegmentIndices.add(index);
            }
            this.updateSegmentExclusion(index);
        });
    }

    toggleSegmentExclusion(index: number, event?: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (this.excludedSegmentIndices.has(index)) {
            this.excludedSegmentIndices.delete(index);
        } else {
            this.excludedSegmentIndices.add(index);
        }
        this.updateSegmentExclusion(index);
    }

    updateSegmentExclusion(index: number): void {
        // Update the label color for this segment
        if (this.segmentLabels[index]) {
            const isExcluded = this.excludedSegmentIndices.has(index);
            const textColor = isExcluded ? '#D9534F' : 'white';
            const bgColor = isExcluded ? '#D9534F' : '#28288C';
            (this.segmentLabels[index] as any).setColor(textColor, bgColor);
        }

        this.measurementResult.distance = this.calculateIncludedDistance();
        this.renderExcludedSegmentPolylines();
        this.cdref.detectChanges();
    }

    isSegmentExcluded(index: number): boolean {
        return this.excludedSegmentIndices.has(index);
    }

    private calculateHoleSegmentDistances(): void {
        this.measurementResult.holeSegmentDistances = [];

        this.measurementHoles.forEach(hole => {
            const holeDistances: number[] = [];

            if (hole.length >= 2) {
                for (let i = 1; i < hole.length; i++) {
                    const segmentDistance = google.maps.geometry.spherical.computeDistanceBetween(hole[i - 1], hole[i]);
                    holeDistances.push(segmentDistance);
                }

                if (hole.length >= 3) {
                    const closingSegmentDistance = google.maps.geometry.spherical.computeDistanceBetween(
                        hole[hole.length - 1],
                        hole[0]
                    );
                    holeDistances.push(closingSegmentDistance);
                }
            }

            this.measurementResult.holeSegmentDistances.push(holeDistances);
        });
    }

    private calculatePolygonAreaWithHoles(): number {
        if (this.measurementOuterRing.length < 3) {
            return 0;
        }

        // Calculate outer ring area
        let totalArea = google.maps.geometry.spherical.computeArea(this.measurementOuterRing);

        // Process each hole
        this.measurementHoles.forEach(hole => {
            if (hole.length >= 3) {
                // Calculate the effective hole area (part of hole inside the polygon)
                const effectiveHoleArea = this.calculateHoleAreaInsidePolygon(hole);
                totalArea -= effectiveHoleArea;
            }
        });

        return totalArea;
    }

    private calculateHoleAreaInsidePolygon(hole: google.maps.LatLng[]): number {
        if (hole.length < 3) {
            return 0;
        }

        try {
            const allPointsInside = hole.every(point => this.isPointInsidePolygon(point, this.measurementOuterRing));
            if (allPointsInside) {
                return google.maps.geometry.spherical.computeArea(hole);
            }

            const somePointInside = hole.some(point => this.isPointInsidePolygon(point, this.measurementOuterRing));
            if (!somePointInside) {
                return 0;
            }

            const outerRingCoords = this.measurementOuterRing.map(point => [point.lng(), point.lat()]);
            outerRingCoords.push(outerRingCoords[0]);

            const holeCoords = hole.map(point => [point.lng(), point.lat()]);
            holeCoords.push(holeCoords[0]);

            const outerPolygon = polygon([outerRingCoords]);
            const holePolygon = polygon([holeCoords]);

            const holeWithin = booleanWithin(holePolygon, outerPolygon);
            if (holeWithin) {
                return area(holePolygon);
            }

            const overlap = booleanOverlap(holePolygon, outerPolygon);
            const intersects = booleanIntersects(holePolygon, outerPolygon);

            if (overlap || intersects) {
                try {
                    const intersection = intersect(featureCollection([holePolygon, outerPolygon]));

                    if (intersection && intersection.geometry.type === 'Polygon') {
                        const intersectionArea = area(intersection);

                        return intersectionArea;
                    } else if (intersection && intersection.geometry.type === 'MultiPolygon') {
                        const intersectionArea = area(intersection);

                        return intersectionArea;
                    }
                } catch (intersectError) {
                    const pointsInside = hole.filter(point =>
                        this.isPointInsidePolygon(point, this.measurementOuterRing)
                    );

                    if (pointsInside.length >= 3) {
                        return google.maps.geometry.spherical.computeArea(pointsInside);
                    }
                }
            }

            return 0;
        } catch (error) {
            console.warn('Error calculating hole area intersection:', error);

            // Fallback: check if hole is completely inside polygon
            const allPointsInside = hole.every(point => this.isPointInsidePolygon(point, this.measurementOuterRing));

            if (allPointsInside) {
                // If all points are inside, use the full hole area
                return google.maps.geometry.spherical.computeArea(hole);
            }

            // If some points are outside, estimate area based on points inside
            const pointsInside = hole.filter(point => this.isPointInsidePolygon(point, this.measurementOuterRing));

            if (pointsInside.length >= 3) {
                // Calculate area of the points that are inside
                return google.maps.geometry.spherical.computeArea(pointsInside);
            }

            return 0;
        }
    }

    clearMeasurement() {
        // Clear segment labels
        this.clearSegmentLabels();

        // Clear outer ring markers
        this.measurementMarkers.forEach(marker => marker.setMap(null));
        this.measurementMarkers = [];

        // Clear hole markers
        this.holeMarkers.forEach(holeMarkerArray => holeMarkerArray.forEach(marker => marker.setMap(null)));
        this.holeMarkers = [];

        if (this.measurementPath) {
            this.measurementPath.setMap(null);
            this.measurementPath = null;
        }

        if (this.measurementPolygon) {
            this.measurementPolygon.setMap(null);
            this.measurementPolygon = null;
        }

        // Clear hole polylines
        this.holePolylines.forEach(polyline => polyline.setMap(null));
        this.holePolylines = [];

        // Clear centroid marker
        if (this.centroidMarker) {
            this.centroidMarker.setMap(null);
            this.centroidMarker = null;
        }

        this.clearExcludedSegmentPolylines();

        // Reset all coordinate arrays
        this.measurementCoordinates = [];
        this.measurementOuterRing = [];
        this.measurementHoles = [];
        this.isCreatingHole = false;
        this.currentHoleIndex = 0;

        this.measurementResult = {
            distance: 0,
            area: 0,
            segmentDistances: [],
            totalSegments: 0,
            holeSegmentDistances: [] as number[][],
            centroid: null,
        };

        this.selectedPointsInPolygon = [];
        this.selectedPointsChange.emit([]);
        this.exportingSelectionExcel = false;

        this.excludedSegmentIndices.clear();
    }

    undoLastMeasurementPoint() {
        if (this.isCreatingHole) {
            this.undoLastHolePoint();
        } else {
            if (this.measurementCoordinates.length === 0) {
                return;
            }
            const lastIndex = this.measurementCoordinates.length - 1;
            this.removeMeasurementPoint(lastIndex);
        }
    }

    private onMarkerDrag(markerIndex: number, newPosition: google.maps.LatLng): void {
        if (markerIndex < this.measurementCoordinates.length) {
            this.measurementCoordinates[markerIndex] = newPosition;
            this.measurementOuterRing[markerIndex] = newPosition;

            if (this.measuring) {
                this.updateAreaMeasurement();
            }
        }
    }

    private onMarkerDragEnd(markerIndex: number, newPosition: google.maps.LatLng): void {
        if (markerIndex < this.measurementCoordinates.length) {
            this.measurementCoordinates[markerIndex] = newPosition;
            this.measurementOuterRing[markerIndex] = newPosition;

            this.measurementMarkers[markerIndex].setTitle(`Buitenrand ${markerIndex + 1} (versleepbaar)`);

            if (this.measuring) {
                this.updateAreaMeasurement();
            }

            this.cdref.detectChanges();
        }
    }

    private removeMeasurementPoint(markerIndex: number): void {
        if (markerIndex < 0 || markerIndex >= this.measurementMarkers.length) {
            return;
        }

        this.measurementMarkers[markerIndex].setMap(null);

        this.measurementMarkers.splice(markerIndex, 1);
        this.measurementCoordinates.splice(markerIndex, 1);
        this.measurementOuterRing.splice(markerIndex, 1);

        this.measurementMarkers.forEach((marker, index) => marker.setTitle(`Buitenrand ${index + 1} (versleepbaar)`));

        if (this.measuring) {
            this.updateAreaMeasurement();
        }

        this.pruneExcludedSegments();

        this.cdref.detectChanges();
    }

    private onHoleMarkerDrag(holeIndex: number, pointIndex: number, newPosition: google.maps.LatLng): void {
        if (holeIndex < this.measurementHoles.length && pointIndex < this.measurementHoles[holeIndex].length) {
            this.measurementHoles[holeIndex][pointIndex] = newPosition;
            if (this.measuring) {
                this.updateAreaMeasurement();
            }
        }
    }

    private onHoleMarkerDragEnd(holeIndex: number, pointIndex: number, newPosition: google.maps.LatLng): void {
        if (holeIndex < this.measurementHoles.length && pointIndex < this.measurementHoles[holeIndex].length) {
            this.measurementHoles[holeIndex][pointIndex] = newPosition;
            this.holeMarkers[holeIndex][pointIndex].setTitle(
                `Uitsnede ${holeIndex + 1} - Punt ${pointIndex + 1} (versleepbaar)`
            );
            if (this.measuring) {
                this.updateAreaMeasurement();
            }
            this.cdref.detectChanges();
        }
    }

    private removeHolePoint(holeIndex: number, pointIndex: number): void {
        if (holeIndex >= this.measurementHoles.length || pointIndex >= this.measurementHoles[holeIndex].length) {
            return;
        }

        this.holeMarkers[holeIndex][pointIndex].setMap(null);
        this.holeMarkers[holeIndex].splice(pointIndex, 1);

        this.measurementHoles[holeIndex].splice(pointIndex, 1);

        this.holeMarkers[holeIndex].forEach((marker, index) =>
            marker.setTitle(`Uitsnede ${holeIndex + 1} - Punt ${index + 1} (versleepbaar)`)
        );

        if (this.measuring) {
            this.updateAreaMeasurement();
        }

        this.cdref.detectChanges();
    }

    undoLastHolePoint(): void {
        if (this.isCreatingHole && this.currentHoleIndex < this.measurementHoles.length) {
            const currentHole = this.measurementHoles[this.currentHoleIndex];
            if (currentHole.length > 0) {
                const lastIndex = currentHole.length - 1;
                this.removeHolePoint(this.currentHoleIndex, lastIndex);
            }
        }
    }

    getTotalHolePoints(): number {
        return this.measurementHoles.reduce((sum, hole) => sum + hole.length, 0);
    }

    private addHolePointFromPolygonClick(latLng: google.maps.LatLng): void {
        if (!this.isCreatingHole || this.currentHoleIndex >= this.measurementHoles.length) {
            return;
        }

        if (!this.isPointInsidePolygon(latLng, this.measurementOuterRing)) {
            return;
        }

        for (const hole of this.measurementHoles) {
            if (hole.length >= 3 && this.isPointInsidePolygon(latLng, hole)) {
                return;
            }
        }

        this.addHolePoint(latLng);
    }

    private addOuterRingPointFromPolygonClick(latLng: google.maps.LatLng): void {
        if (this.measurementOuterRing.length < 3) {
            this.addOuterRingPoint(latLng);
            return;
        }

        let minDistance = Number.MAX_VALUE;
        let insertIndex = this.measurementOuterRing.length;

        for (let i = 0; i < this.measurementOuterRing.length; i++) {
            const current = this.measurementOuterRing[i];
            const next = this.measurementOuterRing[(i + 1) % this.measurementOuterRing.length];

            const distanceToSegment = this.distanceToLineSegment(latLng, current, next);

            if (distanceToSegment < minDistance) {
                minDistance = distanceToSegment;
                insertIndex = i + 1;
            }
        }

        this.measurementOuterRing.splice(insertIndex, 0, latLng);
        this.measurementCoordinates.splice(insertIndex, 0, latLng);

        const marker = this.createMarker(latLng, 'outer', insertIndex);
        this.measurementMarkers.splice(insertIndex, 0, marker);

        this.updateMarkerTitlesAndListeners();

        if (this.measuring) {
            this.updateAreaMeasurement();
        }

        this.cdref.detectChanges();
    }

    private isPointInsidePolygon(point: google.maps.LatLng, polygon: google.maps.LatLng[]): boolean {
        if (polygon.length < 3) {
            return false;
        }

        let inside = false;
        let j = polygon.length - 1;

        for (let i = 0; i < polygon.length; j = i++) {
            const xi = polygon[i].lat();
            const yi = polygon[i].lng();
            const xj = polygon[j].lat();
            const yj = polygon[j].lng();

            if (
                yi > point.lng() !== yj > point.lng() &&
                point.lat() < ((xj - xi) * (point.lng() - yi)) / (yj - yi) + xi
            ) {
                inside = !inside;
            }
        }

        return inside;
    }

    private ensureClockwise(coordinates: google.maps.LatLng[]): google.maps.LatLng[] {
        if (coordinates.length < 3) return coordinates;

        const signedArea = this.calculateSignedArea(coordinates);

        return signedArea > 0 ? coordinates.slice().reverse() : coordinates;
    }

    private ensureCounterClockwise(coordinates: google.maps.LatLng[]): google.maps.LatLng[] {
        if (coordinates.length < 3) return coordinates;

        const signedArea = this.calculateSignedArea(coordinates);

        return signedArea < 0 ? coordinates.slice().reverse() : coordinates;
    }

    private calculateSignedArea(coordinates: google.maps.LatLng[]): number {
        if (coordinates.length < 3) return 0;

        let area = 0;
        for (let i = 0; i < coordinates.length; i++) {
            const j = (i + 1) % coordinates.length;
            area += coordinates[i].lat() * coordinates[j].lng();
            area -= coordinates[j].lat() * coordinates[i].lng();
        }
        return area / 2;
    }

    private distanceToLineSegment(
        mapsPoint: google.maps.LatLng,
        lineStart: google.maps.LatLng,
        lineEnd: google.maps.LatLng
    ): number {
        const pointFeature = point([mapsPoint.lng(), mapsPoint.lat()]);
        const line = lineString([
            [lineStart.lng(), lineStart.lat()],
            [lineEnd.lng(), lineEnd.lat()],
        ]);

        const distanceInKm = pointToLineDistance(pointFeature, line, { units: 'kilometers' });
        return distanceInKm * 1000;
    }

    private createMarker(
        latLng: google.maps.LatLng,
        type: 'outer' | 'hole',
        index: number,
        holeIndex?: number
    ): google.maps.Marker {
        const isOuterRing = type === 'outer';

        const marker = new google.maps.Marker({
            position: latLng,
            map: this.googleMap.googleMap,
            draggable: true,
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 7,
                fillColor: isOuterRing ? '#28288C' : '#FF6B35',
                fillOpacity: 0.9,
                strokeColor: isOuterRing ? '#28288C' : '#FF6B35',
                strokeWeight: 3,
            },
            title: isOuterRing
                ? `Buitenrand ${index + 1} (versleepbaar)`
                : `Uitsnede ${(holeIndex ?? 0) + 1} - Punt ${index + 1} (versleepbaar)`,
            cursor: 'grab',
        });

        if (isOuterRing) {
            this.addGenericMarkerListeners(marker, {
                onDrag: latLng => this.onMarkerDrag(index, latLng!),
                onDragEnd: latLng => this.onMarkerDragEnd(index, latLng!),
                onRightClick: () => this.removeMeasurementPoint(index),
            });
        } else {
            this.addGenericMarkerListeners(marker, {
                onDrag: latLng => this.onHoleMarkerDrag(holeIndex ?? 0, index, latLng!),
                onDragEnd: latLng => this.onHoleMarkerDragEnd(holeIndex ?? 0, index, latLng!),
                onRightClick: () => this.removeHolePoint(holeIndex ?? 0, index),
            });
        }

        return marker;
    }

    private updateMarkerTitlesAndListeners(): void {
        this.measurementMarkers.forEach((marker, index) => {
            marker.setTitle(`Buitenrand ${index + 1} (versleepbaar)`);

            this.addGenericMarkerListeners(marker, {
                onDrag: latLng => this.onMarkerDrag(index, latLng!),
                onDragEnd: latLng => this.onMarkerDragEnd(index, latLng!),
                onRightClick: () => this.removeMeasurementPoint(index),
                clearExistingListeners: true,
            });
        });
    }

    private calculateMidpoint(point1: google.maps.LatLng, point2: google.maps.LatLng): google.maps.LatLng {
        return new google.maps.LatLng((point1.lat() + point2.lat()) / 2, (point1.lng() + point2.lng()) / 2);
    }

    private formatDistanceLabel(distance: number): string {
        if (distance >= 1000) {
            return `${(distance / 1000).toFixed(2)} km`;
        }
        return `${distance.toFixed(2)} m`;
    }

    private createSegmentLabels(): void {
        this.clearSegmentLabels();

        if (this.measurementOuterRing.length >= 2) {
            const points = this.measurementOuterRing;
            const isPolygon = points.length >= 3;

            for (let i = 0; i < points.length; i++) {
                const nextIndex = isPolygon ? (i + 1) % points.length : i + 1;

                if (nextIndex < points.length || isPolygon) {
                    const start = points[i];
                    const end = points[nextIndex];
                    const distance = google.maps.geometry.spherical.computeDistanceBetween(start, end);
                    const midpoint = this.calculateMidpoint(start, end);

                    // Determine color based on exclusion state
                    const isExcluded = this.excludedSegmentIndices.has(i);
                    const backgroundColor = isExcluded ? '#D9534F' : '#28288C';

                    const label = this.createCustomLabel(
                        midpoint,
                        this.formatDistanceLabel(distance),
                        backgroundColor,
                        isExcluded ? '#D9534F' : 'white',
                        event => this.toggleSegmentExclusion(i, event)
                    );

                    this.segmentLabels.push(label);
                }
            }
        }

        this.measurementHoles.forEach((hole, holeIndex) => {
            if (hole.length >= 2) {
                this.holeSegmentLabels[holeIndex] = [];
                const isClosedHole = hole.length >= 3;

                for (let i = 0; i < hole.length; i++) {
                    const nextIndex = isClosedHole ? (i + 1) % hole.length : i + 1;

                    if (nextIndex < hole.length || isClosedHole) {
                        const start = hole[i];
                        const end = hole[nextIndex];
                        const distance = google.maps.geometry.spherical.computeDistanceBetween(start, end);
                        const midpoint = this.calculateMidpoint(start, end);

                        const label = this.createCustomLabel(midpoint, this.formatDistanceLabel(distance), '#FF6B35');

                        this.holeSegmentLabels[holeIndex].push(label);
                    }
                }
            }
        });
    }

    private createCustomLabel(
        position: google.maps.LatLng,
        text: string,
        backgroundColor: string,
        textColor: string = 'white',
        onRightClick?: (event: MouseEvent) => void,
        onClick?: (event: MouseEvent) => void
    ): google.maps.OverlayView {
        class CustomLabel extends google.maps.OverlayView {
            private position: google.maps.LatLng;
            private text: string;
            private backgroundColor: string;
            private textColor: string;
            private onRightClick?: (event: MouseEvent) => void;
            private onClick?: (event: MouseEvent) => void;
            private div: HTMLDivElement | null = null;
            private contextMenuHandler?: (event: MouseEvent) => void;
            private clickHandler?: (event: MouseEvent) => void;

            constructor(
                position: google.maps.LatLng,
                text: string,
                backgroundColor: string,
                textColor: string = 'white',
                onRightClick?: (event: MouseEvent) => void,
                onClick?: (event: MouseEvent) => void
            ) {
                super();
                this.position = position;
                this.text = text;
                this.backgroundColor = backgroundColor;
                this.textColor = textColor;
                this.onRightClick = onRightClick;
                this.onClick = onClick;
            }

            setColor(textColor: string, backgroundColor: string) {
                if (this.div) {
                    this.div.style.color = textColor;
                    this.div.style.background = backgroundColor;
                    this.backgroundColor = backgroundColor;
                }
            }

            onAdd() {
                this.div = document.createElement('div');
                this.div.style.position = 'absolute';
                this.div.style.background = this.backgroundColor;
                this.div.style.color = this.textColor;
                this.div.style.padding = '4px 8px';
                this.div.style.borderRadius = '4px';
                this.div.style.fontSize = '12px';
                this.div.style.fontWeight = 'bold';
                this.div.style.whiteSpace = 'nowrap';
                this.div.style.pointerEvents = 'auto';
                this.div.style.zIndex = '1000';
                this.div.textContent = this.text;

                if (this.onClick) {
                    this.clickHandler = (event: MouseEvent) => {
                        event.preventDefault();
                        event.stopPropagation();
                        this.onClick?.(event);
                    };
                    this.div.addEventListener('click', this.clickHandler);
                }

                if (this.onRightClick) {
                    this.contextMenuHandler = (event: MouseEvent) => {
                        event.preventDefault();
                        event.stopPropagation();
                        this.onRightClick?.(event);
                    };
                    this.div.addEventListener('contextmenu', this.contextMenuHandler);
                }

                const panes = this.getPanes();
                panes?.overlayMouseTarget.appendChild(this.div);
            }

            draw() {
                if (this.div) {
                    const overlayProjection = this.getProjection();
                    const point = overlayProjection.fromLatLngToDivPixel(this.position);

                    if (point) {
                        this.div.style.left = point.x - this.div.offsetWidth / 2 + 'px';
                        this.div.style.top = point.y - this.div.offsetHeight / 2 + 'px';
                    }
                }
            }

            onRemove() {
                if (this.div) {
                    if (this.clickHandler) {
                        this.div.removeEventListener('click', this.clickHandler);
                    }
                    if (this.contextMenuHandler) {
                        this.div.removeEventListener('contextmenu', this.contextMenuHandler);
                    }
                    this.div.parentNode?.removeChild(this.div);
                    this.div = null;
                }
            }
        }

        const label = new CustomLabel(position, text, backgroundColor, textColor, onRightClick, onClick);
        label.setMap(this.googleMap.googleMap);
        return label;
    }

    private clearSegmentLabels(): void {
        this.segmentLabels.forEach(label => label.setMap(null));
        this.segmentLabels = [];

        this.holeSegmentLabels.forEach(holeLabelArray => holeLabelArray.forEach(label => label.setMap(null)));
        this.holeSegmentLabels = [];
    }

    private clearExcludedSegmentPolylines(): void {
        this.excludedSegmentPolylines.forEach(polyline => polyline.setMap(null));
        this.excludedSegmentPolylines = [];
    }

    private renderExcludedSegmentPolylines(): void {
        this.clearExcludedSegmentPolylines();

        if (this.excludedSegmentIndices.size === 0) {
            return;
        }

        for (const index of Array.from(this.excludedSegmentIndices)) {
            const segment = this.getOuterSegmentByIndex(index);
            if (!segment) {
                continue;
            }

            const polyline = new google.maps.Polyline({
                path: [segment.start, segment.end],
                strokeColor: '#D9534F',
                strokeOpacity: 1.0,
                strokeWeight: 3,
                clickable: false,
                map: this.googleMap.googleMap,
            });

            this.excludedSegmentPolylines.push(polyline);
        }
    }

    private getOuterSegmentByIndex(index: number): { start: google.maps.LatLng; end: google.maps.LatLng } | null {
        if (this.measurementOuterRing.length < 2) {
            return null;
        }

        const points = this.measurementOuterRing;
        if (index < 0 || index >= this.measurementResult.segmentDistances.length) {
            return null;
        }

        if (index < points.length - 1) {
            return {
                start: points[index],
                end: points[index + 1],
            };
        }

        if (points.length >= 3 && index === points.length - 1) {
            return {
                start: points[points.length - 1],
                end: points[0],
            };
        }

        return null;
    }

    private calculateAndDisplayCentroid(): void {
        if (this.measurementOuterRing.length < 3) {
            this.measurementResult.centroid = null;
            return;
        }

        try {
            const outerRingCoords = this.measurementOuterRing.map(point => [point.lng(), point.lat()]);
            outerRingCoords.push(outerRingCoords[0]);

            // Build polygon with holes for accurate centroid
            const holeCoordsArray: number[][][] = [];
            this.measurementHoles.forEach(hole => {
                if (hole.length >= 3) {
                    const holeCoords = hole.map(point => [point.lng(), point.lat()]);
                    holeCoords.push(holeCoords[0]);
                    holeCoordsArray.push(holeCoords);
                }
            });

            const polygonFeature = polygon([outerRingCoords, ...holeCoordsArray]);
            const polygonCentroid = centroid(polygonFeature);
            const [lng, lat] = polygonCentroid.geometry.coordinates;

            this.measurementResult.centroid = new google.maps.LatLng(lat, lng);
        } catch (error) {
            console.warn('Error calculating centroid:', error);
            this.measurementResult.centroid = null;
        }
    }
}
